"use client";

import * as React from "react";
import { Field as FieldPrimitive } from "@base-ui/react/field";

import { ProblemMark, ReasonLine } from "@/components/ui/reason-line";
import { cn } from "@/lib/utils";

/**
 * One field: its label, the control (Input, Textarea, Select… inside it are wired to it), a description and its problem.
 * The label names the control and the description describes it (`aria-describedby`). A problem marks the control invalid,
 * draws the amber "!" inside the control's right edge and writes its reason on the label line, right-aligned, so the
 * form below does not move; the reason describes the control too (docs/ui-conventions.md, "A field with a problem").
 */
function Field({ className, ...props }: FieldPrimitive.Root.Props) {
  return (
    <FieldPrimitive.Root
      data-slot="field"
      className={cn(
        "group/field relative grid grid-cols-[auto_minmax(0,1fr)] content-start items-center gap-x-0 gap-y-1.5",
        // The label keeps its full width and the reason takes what is left of the first line, cut first; everything else takes the full width under it.
        "[&>:not([data-slot=field-label],[data-slot=field-error])]:col-span-2",
        // Room for the "!" at the end of a text box that has a problem.
        "data-invalid:[&_:is(input,textarea)]:!pr-9",
        className
      )}
      {...props}
    />
  );
}

/** The field's name. `required` adds the mark; the control carries `required` itself. */
function FieldLabel({
  className,
  required = false,
  children,
  ...props
}: FieldPrimitive.Label.Props & { required?: boolean }) {
  return (
    <FieldPrimitive.Label
      data-slot="field-label"
      className={cn(
        "col-start-1 row-start-1 flex items-center gap-1 text-body-tight font-medium whitespace-nowrap select-none group-data-disabled/field:opacity-50",
        className
      )}
      {...props}
    >
      {children}
      {required ? (
        <span aria-hidden className="text-muted-foreground">
          *
        </span>
      ) : null}
    </FieldPrimitive.Label>
  );
}

/** A line of help under the control: what to enter, never what went wrong. */
function FieldDescription({ className, ...props }: FieldPrimitive.Description.Props) {
  return (
    <FieldPrimitive.Description
      data-slot="field-description"
      className={cn("text-caption text-muted-foreground", className)}
      {...props}
    />
  );
}

/**
 * The two marks of one problem (a field marked with no words, an empty required one, has the "!" alone): the reason on the label line (`ReasonLine`, which the control is described by) and the amber
 * "!" inside the control. Both hold the full words in a tooltip, so where the label leaves the reason no room (a very narrow
 * column) the "!" alone still says it.
 */
function FieldReason({ children, className, ...props }: React.ComponentProps<"div">) {
  return (
    <>
      {children ? (
        <ReasonLine className={cn("col-start-2 row-start-1 pl-3", className)} {...props}>
          {children}
        </ReasonLine>
      ) : null}
      <ProblemMark
        reason={children}
        className="top-2.5 right-3 group-has-[[data-slot=field-label]]/field:top-9 group-has-[>[data-slot=select-trigger]]/field:right-9 group-has-[[data-align=inline-end]]/field:right-9"
      />
    </>
  );
}

/**
 * What is wrong with the value, once the field is invalid: the native check's message, or the schema's or the server's, as
 * `FieldReason`. A problem the screen watches as the value is typed (a phone number that cannot be one) is `<Field invalid>`
 * with `<FieldError match>`.
 */
function FieldError({
  className,
  match,
  children,
}: {
  className?: string;
  /** `true` shows the reason now, for a problem the screen watches as the value is typed (hand it with `children` and the Field's `invalid`). */
  match?: boolean;
  /** The screen's own words for that problem; by default the native check's, the schema's or the server's. */
  children?: React.ReactNode;
}) {
  return (
    <FieldPrimitive.Error
      data-slot="field-error"
      match={match}
      render={<FieldReason />}
      className={className}
      // Base UI shows the message itself unless it is handed children, so none is passed when there are none.
      {...(children === undefined ? {} : { children })}
    />
  );
}

export { Field, FieldLabel, FieldDescription, FieldError };
